import React, {
  useContext, useRef, useState, useEffect,
} from 'react';
import {ChevronRightIcon} from '@heroicons/react/outline';

import Header from '../../components/Header';
import ConfirmNewAccountModal from '../../components/ConfirmNewAccountModal';
import FeedbackModal, {shouldShowFeedbackModal} from '../../components/FeedbackModal';
import CourseSidebar from './CourseSidebar';
import {CourseLessonsContext} from '../../components/CourseLessonsProvider';
import EditorPanel, {shouldShowEditor} from '../../components/code/EditorPanel';
import {Panel, PanelGroup, PanelResizer} from '../../components/Panel';
import {useAppContext, useAnalytics} from '../../hooks';

const LessonContainer = ({
  children, lessonSlug, courseSlug, trackSlug,
}) => {
  const {pendingAccount, currentUser, hasSubscription} = useAppContext();
  const {tracker} = useAnalytics();
  const {currentLesson: lesson} = useContext(CourseLessonsContext);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [initialDraft, setInitialDraft] = useState();
  const sidebarRef = useRef(null);
  const showEditorPanel = shouldShowEditor(lesson);

  useEffect(() => {
    if (shouldShowFeedbackModal(currentUser, hasSubscription)) {
      setShowFeedbackModal(true);
    }
  }, [hasSubscription, currentUser]);

  const onSidebarCollapse = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  const openSidebar = () => {
    sidebarRef?.current?.expand();
  };

  const onEditCode = ({language, ...rest}) => {
    tracker.codeEditorEditCode({
      type: 'lesson', lesson: lesson.slug, language, difficulty: lesson.difficulty, ...rest,
    });
  };

  const onRunCode = ({language, ...rest}) => {
    tracker.codeEditorRunCode({
      type: 'lesson', lesson: lesson.slug, language, difficulty: lesson.difficulty, ...rest,
    });
  };

  const onRunTests = ({language, ...rest}) => {
    tracker.codeEditorRunTests({
      type: 'lesson', lesson: lesson.slug, language, difficulty: lesson.difficulty, ...rest,
    });
  };

  const onPassTests = ({language, ...rest}) => {
    tracker.codeEditorPassedTests({
      type: 'lesson', lesson: lesson.slug, language, difficulty: lesson.difficulty, ...rest,
    });
  };

  const onShareCode = ({language, content}) => {
    const draft = `\n\n\`\`\`${language}\n${content}\n\`\`\``;
    localStorage.setItem(`draftComment-${lesson.questionId}`,
      JSON.stringify({
        time: Date.now(),
        data: draft,
      }));
    setInitialDraft({draft, time: Date.now()});
    tracker.codeEditorClickShare({
      type: 'lesson', lesson: lesson.slug, language, difficulty: lesson.difficulty,
    });
  };

  return (
    <>
      {pendingAccount && <ConfirmNewAccountModal />}
      <FeedbackModal
        courseSlug={trackSlug || courseSlug}
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
      <Header isFixed upgradeLink={`/upgrade?course=${trackSlug || courseSlug}&src=nav`} />

      {/* Desktop version of page with panels */}
      <div className="bg-white flex pt-16 h-screen">
        <PanelGroup direction="horizontal">
          <Panel
            defaultSize={20}
            minSize={20}
            maxSize={25}
            collapsible
            onCollapse={onSidebarCollapse}
            className="hidden lg:flex"
            ref={sidebarRef}
          >
            <div className="bg-white h-full max-h-screen overflow-auto">
              <CourseSidebar
                lessonSlug={lessonSlug}
                courseSlug={courseSlug}
                trackSlug={trackSlug}
              />
            </div>
          </Panel>
          <PanelResizer className="hidden lg:flex" />
          <Panel defaultSize={showEditorPanel ? 45 : 80} minSize={30} maxSize={100}>
            {typeof children === 'function' ? children({initialDraft}) : children}
          </Panel>
          <PanelResizer className="hidden lg:flex" />
          {showEditorPanel && (
            <Panel minSize={20} defaultSize={35} collapsible className="hidden lg:flex">
              <EditorPanel
                lesson={lesson}
                onEditCode={onEditCode}
                onRunCode={onRunCode}
                onRunTests={onRunTests}
                onPassTests={onPassTests}
                onShareCode={onShareCode}
              />
            </Panel>
          )}
        </PanelGroup>
        {sidebarCollapsed && (
          <button
            type="button"
            className="absolute bottom-4 left-3 text-gray-400 hover:text-gray-500"
            onClick={openSidebar}
          >
            <ChevronRightIcon className="h-5 w-5" />
            <span className="sr-only">Show sidebar</span>
          </button>
        )}
      </div>
    </>
  );
};

export default LessonContainer;
